import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Activity,
  CalendarDays,
  Dumbbell,
  Flag,
  GitCompare,
  HeartPulse,
  ListChecks,
  LogOut,
  RefreshCw,
  Search,
  Settings,
  Sun,
  Trophy,
} from 'lucide-react'
import { dateOf } from '@gd/shared/dates'
import { useActivities, useAthlete, useAthletes, useMe } from '@/api/hooks'
import { useSignOut } from '@/api/auth'
import { Dialog } from '@/components/ui/Dialog'
import { AthleteDot } from '@/components/ui/AthleteDot'
import { SportBadge } from '@/components/ui/SportBadge'
import { cn } from '@/lib/cn'
import { formatDate, formatDuration } from '@/lib/format'

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PaletteItem {
  id: string
  icon?: ComponentType<{ className?: string }>
  label: ReactNode
  meta?: ReactNode
  onSelect: () => void
}

interface PaletteGroup {
  key: string
  label: string
  items: PaletteItem[]
}

const PAGE_ROUTES: {
  key: string
  label: string
  icon: ComponentType<{ className?: string }>
  path: string
}[] = [
  { key: 'today', label: 'Today', icon: Sun, path: 'today' },
  { key: 'plan', label: 'Plan', icon: Flag, path: 'plan' },
  { key: 'fitness', label: 'Fitness', icon: Activity, path: 'fitness' },
  { key: 'wellness', label: 'Wellness', icon: HeartPulse, path: 'wellness' },
  { key: 'activities', label: 'Activities', icon: ListChecks, path: 'activities' },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays, path: 'calendar' },
  { key: 'records', label: 'Records', icon: Trophy, path: 'records' },
  { key: 'strength', label: 'Strength', icon: Dumbbell, path: 'strength' },
  { key: 'settings', label: 'Settings', icon: Settings, path: 'settings' },
]

function matches(query: string, ...values: (string | null | undefined)[]): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  return values.some((v) => v?.toLowerCase().includes(q))
}

/**
 * ⌘K / Ctrl+K command palette: jump to a page, switch athlete, or find a recent activity.
 * A small, purpose-built list/keyboard implementation rather than an external cmdk dependency.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { data: athletes } = useAthletes()
  const { athlete } = useAthlete(slug)
  const activitiesQuery = useActivities(athlete?.id, { limit: 200 })
  const { data: me } = useMe()
  const { signOut } = useSignOut()
  const canSignOut = me?.auth === 'session'

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Global ⌘K / Ctrl+K shortcut, active app-wide regardless of the palette's own mount state.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onOpenChange])

  // Reset the search on each open, per the "adjust state while rendering" pattern (avoids a
  // setState-in-effect render cascade for what is really a derived reset, not a subscription).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setQuery('')
      setActiveIndex(0)
    }
  }

  useEffect(() => {
    if (!open) return undefined
    // Radix moves focus to the dialog content on open; steal it back for the input.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  const effectiveSlug = slug ?? athletes?.[0]?.slug

  const goTo = (path: string) => {
    navigate(path)
    onOpenChange(false)
  }

  const groups = useMemo<PaletteGroup[]>(() => {
    const result: PaletteGroup[] = []

    const pageItems: PaletteItem[] = []
    for (const route of PAGE_ROUTES) {
      if (matches(query, route.label)) {
        pageItems.push({
          id: `page-${route.key}`,
          icon: route.icon,
          label: route.label,
          onSelect: () => goTo(`/a/${effectiveSlug}/${route.path}`),
        })
      }
    }
    if (matches(query, 'compare')) {
      pageItems.push({
        id: 'page-compare',
        icon: GitCompare,
        label: 'Compare',
        onSelect: () => goTo('/compare'),
      })
    }
    if (matches(query, 'sync')) {
      pageItems.push({
        id: 'page-sync',
        icon: RefreshCw,
        label: 'Sync',
        onSelect: () => goTo('/sync'),
      })
    }
    if (pageItems.length > 0) result.push({ key: 'pages', label: 'Pages', items: pageItems })

    const athleteItems: PaletteItem[] = (athletes ?? [])
      .filter((a) => matches(query, a.name, a.slug))
      .map((a) => ({
        id: `athlete-${a.slug}`,
        label: (
          <span className="inline-flex items-center gap-2">
            <AthleteDot color={a.color} />
            {a.name}
          </span>
        ),
        onSelect: () => {
          const next = slug
            ? location.pathname.replace(`/a/${slug}`, `/a/${a.slug}`)
            : `/a/${a.slug}/today`
          navigate({ pathname: next, search: location.search })
          onOpenChange(false)
        },
      }))
    if (athleteItems.length > 0)
      result.push({ key: 'athletes', label: 'Athletes', items: athleteItems })

    if (canSignOut && matches(query, 'sign out', 'log out', 'account')) {
      result.push({
        key: 'account',
        label: 'Account',
        items: [
          {
            id: 'account-sign-out',
            icon: LogOut,
            label: 'Sign out',
            meta: me?.email ? (
              <span className="min-w-0 truncate text-caption text-ink-3">{me.email}</span>
            ) : undefined,
            onSelect: () => {
              onOpenChange(false)
              void signOut()
            },
          },
        ],
      })
    }

    if (query) {
      const flatActivities = activitiesQuery.data?.pages.flatMap((p) => p.items) ?? []
      const activityItems: PaletteItem[] = flatActivities
        .filter((a) =>
          matches(query, a.name, a.type, formatDate(dateOf(a.startDateLocal), { weekday: false })),
        )
        .slice(0, 8)
        .map((a) => ({
          id: `activity-${a.id}`,
          label: (
            <span className="inline-flex min-w-0 items-center gap-2">
              <SportBadge sport={a.sportGroup} dotOnly />
              <span className="truncate">{a.name ?? a.type}</span>
            </span>
          ),
          meta: (
            <span className="shrink-0 text-caption text-ink-3">
              {formatDate(dateOf(a.startDateLocal), { weekday: false })},{' '}
              {formatDuration(a.movingTime)}
            </span>
          ),
          onSelect: () => goTo(`/a/${effectiveSlug}/activities/${a.id}`),
        }))
      if (activityItems.length > 0)
        result.push({ key: 'activities', label: 'Activities', items: activityItems })
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    athletes,
    activitiesQuery.data,
    effectiveSlug,
    slug,
    location.pathname,
    location.search,
    canSignOut,
    me?.email,
  ])

  const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups])

  // Same "adjust state while rendering" pattern: re-point the selection at the top result
  // whenever the query changes the result list, without a setState-in-effect cascade.
  const [prevQuery, setPrevQuery] = useState(query)
  if (query !== prevQuery) {
    setPrevQuery(query)
    setActiveIndex(0)
  }

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (flatItems.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % flatItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      flatItems[activeIndex]?.onSelect()
    }
  }

  let runningIndex = -1

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Search" className="max-w-lg">
      <div className="flex items-center gap-2 rounded-md border border-line-strong bg-surface-2 px-3">
        <Search className="size-4 shrink-0 text-ink-3" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-list"
          aria-activedescendant={flatItems[activeIndex]?.id}
          placeholder="Search pages, athletes, activities…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-11 w-full bg-transparent text-body text-ink outline-none placeholder:text-ink-3"
        />
      </div>

      <div id="command-palette-list" role="listbox" className="mt-3 max-h-[360px] overflow-y-auto">
        {flatItems.length === 0 ? (
          <p className="px-2 py-6 text-center text-small text-ink-2">No results</p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="mb-2 last:mb-0">
              <p className="px-2 py-1 text-caption font-medium text-ink-3">{group.label}</p>
              <ul>
                {group.items.map((item) => {
                  runningIndex += 1
                  const index = runningIndex
                  const active = index === activeIndex
                  return (
                    <li key={item.id}>
                      <button
                        id={item.id}
                        role="option"
                        aria-selected={active}
                        type="button"
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => item.onSelect()}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 rounded-sm px-2 py-2 text-left text-small text-ink',
                          active
                            ? 'bg-[color-mix(in_oklab,var(--ink)_8%,transparent)]'
                            : 'hover:bg-[color-mix(in_oklab,var(--ink)_5%,transparent)]',
                        )}
                      >
                        <span className="inline-flex min-w-0 items-center gap-2">
                          {item.icon && (
                            <item.icon className="size-4 shrink-0 text-ink-2" aria-hidden="true" />
                          )}
                          {item.label}
                        </span>
                        {item.meta}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </Dialog>
  )
}
